// Registry table — the main view. Renders every audited store, sorted
// worst-first by score percentage (where GTM action lives).
//
// Mirrors `tools/image-studio/ResultsSection.jsx` shape: Cloudscape
// `<Container>` + `<Header variant="h2">` with action buttons inline,
// `<Table variant="embedded">` with `<StatusIndicator>` cells, refresh
// button. Score-pill cell uses the traffic-light coloring per the same
// rules as `generate_excel.py::score_fill` from the POC.
//
// Filters live in the Header `actions` slot — search / score band /
// trigger kind / has-LCP. Decision #22 in the tracker.

import React, { useEffect, useMemo, useState } from 'react';
import {
  Container,
  Header,
  Table,
  Box,
  Button,
  Input,
  Select,
  SpaceBetween,
  StatusIndicator,
  Link as CSLink,
} from '@cloudscape-design/components';

import PerStoreHistoryModal from './PerStoreHistoryModal.jsx';
import { storeCROAuditApi } from './storeCROAuditApi.js';

const SCORE_BAND_OPTIONS = [
  { label: 'All scores', value: 'all' },
  { label: '🔴 Red — below 50%', value: 'red' },
  { label: '🟡 Yellow — 50% to 80%', value: 'yellow' },
  { label: '🟢 Green — 80% and above', value: 'green' },
];

const TRIGGER_OPTIONS = [
  { label: 'All triggers', value: 'all' },
  { label: 'Last run: scheduled', value: 'scheduled' },
  { label: 'Last run: ondemand', value: 'ondemand' },
];

export default function RegistryTable({
  refreshTrigger,
  onRefresh,
  onError,
  onSwitchToAudit,
  username,
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [scoreBand, setScoreBand] = useState(SCORE_BAND_OPTIONS[0]);
  const [triggerKind, setTriggerKind] = useState(TRIGGER_OPTIONS[0]);
  const [exporting, setExporting] = useState(false);

  // Per-store history modal state.
  const [historyShopId, setHistoryShopId] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);

  const loadRegistry = async () => {
    setLoading(true);
    try {
      const data = await storeCROAuditApi.getRegistry();
      setRows(data?.stores || []);
    } catch (err) {
      onError?.(err.message || 'Failed to load registry');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRegistry();
  }, [refreshTrigger]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredRows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return rows
      .filter((r) => {
        if (needle) {
          const haystack = [
            r.store_name || '',
            r.store_url || '',
            String(r.shop_id || ''),
          ]
            .join(' ')
            .toLowerCase();
          if (!haystack.includes(needle)) return false;
        }
        if (scoreBand.value !== 'all') {
          const pct = Number(r.latest_score_percentage || 0);
          if (scoreBand.value === 'red' && pct >= 50) return false;
          if (scoreBand.value === 'yellow' && (pct < 50 || pct >= 80)) return false;
          if (scoreBand.value === 'green' && pct < 80) return false;
        }
        if (triggerKind.value !== 'all' && r.latest_run_kind !== triggerKind.value) {
          return false;
        }
        return true;
      })
      .sort(
        (a, b) =>
          Number(a.latest_score_percentage || 0) - Number(b.latest_score_percentage || 0)
      );
  }, [rows, search, scoreBand, triggerKind]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const filters = {
        score_band: scoreBand.value,
        trigger_kind: triggerKind.value,
        // The frontend already filtered; pass the current shop_ids if
        // either filter is non-default so the export matches what's on
        // screen.
        shop_ids:
          scoreBand.value !== 'all' || triggerKind.value !== 'all'
            ? filteredRows.map((r) => r.shop_id)
            : undefined,
      };
      const { url } = await storeCROAuditApi.generateExport(filters);
      // apiFetch-then-presigned-URL pattern. Browser navigates to S3
      // directly; no JWT needed at the click step because S3 verifies
      // the presigned signature.
      window.location.href = url;
    } catch (err) {
      onError?.(err.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const openHistory = (shopId) => {
    setHistoryShopId(shopId);
    setHistoryOpen(true);
  };

  return (
    <>
      <Container
        header={
          <Header
            variant="h2"
            counter={`(${filteredRows.length})`}
            description="Click a row to view that store's audit history. Worst-performing stores listed first."
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <Input
                  type="search"
                  placeholder="Search store, URL, shop_id"
                  value={search}
                  onChange={({ detail }) => setSearch(detail.value)}
                />
                <Select
                  selectedOption={scoreBand}
                  onChange={({ detail }) => setScoreBand(detail.selectedOption)}
                  options={SCORE_BAND_OPTIONS}
                />
                <Select
                  selectedOption={triggerKind}
                  onChange={({ detail }) => setTriggerKind(detail.selectedOption)}
                  options={TRIGGER_OPTIONS}
                />
                <Button
                  iconName="refresh"
                  onClick={() => {
                    onRefresh?.();
                    loadRegistry();
                  }}
                  loading={loading}
                >
                  Refresh
                </Button>
                <Button iconName="download" onClick={handleExport} loading={exporting}>
                  Export all
                </Button>
                <Button
                  variant="primary"
                  iconName="add-plus"
                  onClick={() => onSwitchToAudit?.()}
                >
                  Audit stores
                </Button>
              </SpaceBetween>
            }
          >
            📋 Stores in scope
          </Header>
        }
      >
        <Table
          variant="embedded"
          items={filteredRows}
          loading={loading}
          loadingText="Loading registry..."
          sortingDisabled
          columnDefinitions={[
            {
              id: 'score',
              header: 'Score',
              cell: scoreCell,
              width: 130,
            },
            {
              id: 'store',
              header: 'Store',
              cell: (item) => storeCell(item, () => openHistory(item.shop_id)),
              width: 220,
            },
            {
              id: 'shop_id',
              header: 'Shop ID',
              cell: (item) => item.shop_id,
              width: 90,
            },
            {
              id: 'last_run',
              header: 'Last run',
              cell: lastRunCell,
              width: 200,
            },
            {
              id: 'trigger',
              header: 'Trigger',
              cell: triggerCell,
              width: 130,
            },
            {
              id: 'runs',
              header: 'Runs',
              cell: (item) => item.run_count || 0,
              width: 70,
            },
            {
              id: 'actions',
              header: '',
              cell: (item) => actionsCell(item, () => openHistory(item.shop_id), onSwitchToAudit),
              width: 200,
            },
          ]}
          empty={
            <Box textAlign="center" color="inherit" padding="l">
              <SpaceBetween size="xxs">
                <Box variant="p" color="inherit">
                  <b>No stores audited yet</b>
                </Box>
                <Box variant="p" color="text-body-secondary">
                  Run your first audit to add a store to the registry.
                </Box>
                <Button variant="primary" onClick={() => onSwitchToAudit?.()}>
                  Audit a store
                </Button>
              </SpaceBetween>
            </Box>
          }
        />
      </Container>

      {historyShopId !== null && (
        <PerStoreHistoryModal
          visible={historyOpen}
          shopId={historyShopId}
          onDismiss={() => setHistoryOpen(false)}
          onError={onError}
          onReauditDone={() => {
            setHistoryOpen(false);
            onRefresh?.();
          }}
          username={username}
        />
      )}
    </>
  );
}

// ── Cell renderers ────────────────────────────────────────────────────────

function scoreCell(item) {
  // Show "Failed" badge for stores whose latest run failed
  if (item.latest_status === 'failed') {
    const errorMsg = item.latest_error || 'Audit failed — click View history for details';
    return (
      <span
        className="tu-pill-trigger"
        onMouseEnter={(e) => {
          const tip = e.currentTarget.querySelector('.tu-pill-tip');
          if (tip) {
            const rect = e.currentTarget.getBoundingClientRect();
            tip.style.top = `${rect.bottom + 4}px`;
            tip.style.left = `${rect.left}px`;
          }
        }}
      >
        <span className="cro-audit-score-pill red">✗ Failed</span>
        <span className="tu-pill-tip cro-audit-error-tip" role="tooltip">{errorMsg}</span>
      </span>
    );
  }
  const score = Number(item.latest_score || 0);
  const max = Number(item.latest_max_score || 0);
  const pct = Number(item.latest_score_percentage || 0);
  const band = bandFor(pct);
  return (
    <span className={`cro-audit-score-pill ${band}`}>
      {score.toFixed(1)} / {max.toFixed(1)} ({pct.toFixed(0)}%)
    </span>
  );
}

function storeCell(item, onClick) {
  // Using a button styled as a link for the drill-down so the keyboard /
  // screen-reader story is correct. The actual store URL is rendered as a
  // separate Cloudscape <Link> below, opening in a new tab.
  return (
    <SpaceBetween size="xxs">
      <button
        type="button"
        onClick={onClick}
        style={{
          background: 'none',
          border: 'none',
          padding: 0,
          color: '#0f172a',
          cursor: 'pointer',
          fontWeight: 600,
          textAlign: 'left',
        }}
      >
        {item.store_name || '(unnamed)'}
      </button>
      <CSLink
        href={item.store_url.startsWith('http') ? item.store_url : `https://${item.store_url.includes('.') ? item.store_url : item.store_url + '.smartbiz.in'}`}
        external
        externalIconAriaLabel="Opens in a new tab"
      >
        {item.store_url}
      </CSLink>
    </SpaceBetween>
  );
}

function lastRunCell(item) {
  if (!item.latest_run_at) return '—';
  const formatted = formatTimestamp(item.latest_run_at);
  // Yellow badge for scoring-version mismatch (decision #21). The current
  // version is hardcoded here to keep the cell renderer pure; surface as
  // a small piece of state if/when the scorer bumps and we need parity
  // with what the backend reports.
  const CURRENT_VERSION = '1.2.0';
  const showBadge =
    item.scoring_version && item.scoring_version !== CURRENT_VERSION;
  return (
    <SpaceBetween direction="horizontal" size="xxs">
      <span>{formatted}</span>
      {showBadge && (
        <span
          className="cro-audit-version-badge"
          title={`Computed under scoring v${item.scoring_version}. Current scorer is v${CURRENT_VERSION}. Scores not directly comparable across rule changes.`}
        >
          ⚠ v{item.scoring_version}
        </span>
      )}
    </SpaceBetween>
  );
}

function triggerCell(item) {
  if (item.latest_run_kind === 'scheduled') {
    return <StatusIndicator type="info">scheduled</StatusIndicator>;
  }
  if (item.latest_run_kind === 'ondemand') {
    const u = item.latest_run_username || 'unknown';
    return (
      <StatusIndicator type="pending" colorOverride="grey">
        ↑ {u}
      </StatusIndicator>
    );
  }
  return '—';
}

function actionsCell(item, onHistory, onSwitchToAudit) {
  return (
    <SpaceBetween direction="horizontal" size="xxs">
      <Button variant="inline-link" onClick={onHistory}>
        View history
      </Button>
      {/* Re-audit kicks off a single-store audit and switches view. */}
      <Button
        variant="inline-link"
        iconName="refresh"
        onClick={() => {
          // Store the target in sessionStorage so RunAuditPanel can pre-fill
          // the form fields with this store's details.
          try {
            sessionStorage.setItem(
              'cro-audit-reaudit',
              JSON.stringify({ shop_id: item.shop_id, store_url: item.store_url })
            );
          } catch {
            /* ignore */
          }
          onSwitchToAudit?.();
        }}
      >
        Re-audit
      </Button>
    </SpaceBetween>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────

function bandFor(percentage) {
  const pct = Number(percentage || 0);
  if (pct >= 80) return 'green';
  if (pct >= 50) return 'yellow';
  if (pct > 0) return 'red';
  return 'grey';
}

function formatTimestamp(isoOrTimestamp) {
  if (!isoOrTimestamp) return '—';
  // `latest_run_at` may use the dash-replaced timestamp shape from the
  // run's filename ("2026-06-23T04-12-09Z"). Normalise.
  const normalized = String(isoOrTimestamp).replace(
    /T(\d{2})-(\d{2})-(\d{2})Z$/,
    'T$1:$2:$3Z'
  );
  const dt = new Date(normalized);
  if (Number.isNaN(dt.getTime())) return isoOrTimestamp;
  return (
    dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) +
    ' · ' +
    dt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })
  );
}
